import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";

export const bankTransfersSlice = createSlice({
    name: "bankTransfers",
    initialState: {
        // List bank transfers
        listLoading: false,
        listSuccess: false,
        listFail: false,
        listData: null,
        listPage: 0,
        listPageCount: 0,
        listStatistics: null,
        listErrorMessage: null,

        // Get transfer detail
        detailLoading: false,
        detailSuccess: false,
        detailFail: false,
        detailData: null,
        detailErrorMessage: null,

        // Approve transfer
        approveLoading: false,
        approveSuccess: false,
        approveFail: false,
        approveData: null,
        approveErrorMessage: null,

        // Reject transfer
        rejectLoading: false,
        rejectSuccess: false,
        rejectFail: false,
        rejectData: null,
        rejectErrorMessage: null,
    },
    reducers: {
        // List Bank Transfers Actions
        doGetBankTransfersListRequest: (state) => {
            state.listLoading = true;
            state.listSuccess = false;
            state.listFail = false;
            state.listErrorMessage = null;
        },
        doGetBankTransfersListSuccess: (state, action) => {
            state.listLoading = false;
            state.listSuccess = true;
            state.listFail = false;
            state.listData = action.payload?.transfers || [];
            state.listPage = action.payload?.pagination?.current_page || 1;
            state.listPageCount = action.payload?.pagination?.page_count || 0;
            state.listStatistics = action.payload?.statistics || null;
            state.listErrorMessage = null;
        },
        doGetBankTransfersListFail: (state, action) => {
            state.listLoading = false;
            state.listSuccess = false;
            state.listFail = true;
            state.listData = null;
            state.listPage = 0;
            state.listPageCount = 0;
            state.listStatistics = null;
            state.listErrorMessage = action.payload;
        },

        // Get Transfer Detail Actions
        doGetBankTransferDetailRequest: (state) => {
            state.detailLoading = true;
            state.detailSuccess = false;
            state.detailFail = false;
            state.detailErrorMessage = null;
        },
        doGetBankTransferDetailSuccess: (state, action) => {
            state.detailLoading = false;
            state.detailSuccess = true;
            state.detailFail = false;
            state.detailData = action.payload.data;
            state.detailErrorMessage = null;
        },
        doGetBankTransferDetailFail: (state, action) => {
            state.detailLoading = false;
            state.detailSuccess = false;
            state.detailFail = true;
            state.detailData = null;
            state.detailErrorMessage = action.payload;
        },

        // Approve Transfer Actions
        doApproveBankTransferRequest: (state) => {
            state.approveLoading = true;
            state.approveSuccess = false;
            state.approveFail = false;
            state.approveErrorMessage = null;
        },
        doApproveBankTransferSuccess: (state, action) => {
            state.approveLoading = false;
            state.approveSuccess = true;
            state.approveFail = false;
            state.approveData = action.payload.data;
            state.approveErrorMessage = null;
        },
        doApproveBankTransferFail: (state, action) => {
            state.approveLoading = false;
            state.approveSuccess = false;
            state.approveFail = true;
            state.approveData = null;
            state.approveErrorMessage = action.payload;
        },

        // Reject Transfer Actions
        doRejectBankTransferRequest: (state) => {
            state.rejectLoading = true;
            state.rejectSuccess = false;
            state.rejectFail = false;
            state.rejectErrorMessage = null;
        },
        doRejectBankTransferSuccess: (state, action) => {
            state.rejectLoading = false;
            state.rejectSuccess = true;
            state.rejectFail = false;
            state.rejectData = action.payload.data;
            state.rejectErrorMessage = null;
        },
        doRejectBankTransferFail: (state, action) => {
            state.rejectLoading = false;
            state.rejectSuccess = false;
            state.rejectFail = true;
            state.rejectData = null;
            state.rejectErrorMessage = action.payload;
        },

        // Reset Actions
        resetBankTransfersList: (state) => {
            state.listLoading = false;
            state.listSuccess = false;
            state.listFail = false;
            state.listErrorMessage = null;
        },
        resetBankTransferDetail: (state) => {
            state.detailLoading = false;
            state.detailSuccess = false;
            state.detailFail = false;
            state.detailData = null;
            state.detailErrorMessage = null;
        },
        resetApproveTransfer: (state) => {
            state.approveLoading = false;
            state.approveSuccess = false;
            state.approveFail = false;
            state.approveData = null;
            state.approveErrorMessage = null;
        },
        resetRejectTransfer: (state) => {
            state.rejectLoading = false;
            state.rejectSuccess = false;
            state.rejectFail = false;
            state.rejectData = null;
            state.rejectErrorMessage = null;
        },
    },
});

// Export actions
export const {
    doGetBankTransfersListRequest,
    doGetBankTransfersListSuccess,
    doGetBankTransfersListFail,
    doGetBankTransferDetailRequest,
    doGetBankTransferDetailSuccess,
    doGetBankTransferDetailFail,
    doApproveBankTransferRequest,
    doApproveBankTransferSuccess,
    doApproveBankTransferFail,
    doRejectBankTransferRequest,
    doRejectBankTransferSuccess,
    doRejectBankTransferFail,
    resetBankTransfersList,
    resetBankTransferDetail,
    resetApproveTransfer,
    resetRejectTransfer,
} = bankTransfersSlice.actions;

// Async thunk actions
export const doGetBankTransfersList = (params) => async (dispatch) => {
    try {
        dispatch(doGetBankTransfersListRequest());

        const {
            page = 1,
            limit = 20,
            status = "pending",
            search = "",
            from_date = "",
            to_date = "",
            sort_by = "created_at",
            sort_order = "desc",
        } = params;

        let url = `/api/admin/credit-packages/bank-transfers?page=${page}&size=${limit}`;

        if (status) {
            url += `&status=${status}`;
        }
        if (search) {
            url += `&search=${encodeURIComponent(search)}`;
        }
        if (from_date) {
            url += `&from_date=${from_date}`;
        }
        if (to_date) {
            url += `&to_date=${to_date}`;
        }
        if (sort_by) {
            url += `&sort_by=${sort_by}`;
        }
        if (sort_order) {
            url += `&sort_order=${sort_order}`;
        }

        const response = await axios.get(url);
        dispatch(doGetBankTransfersListSuccess(response.data));
    } catch (error) {
        const errorMessage = error.response?.data?.message || "Failed to fetch bank transfers";
        dispatch(doGetBankTransfersListFail(errorMessage));
    }
};

export const doGetBankTransferDetail = (transferId) => async (dispatch) => {
    try {
        dispatch(doGetBankTransferDetailRequest());

        const url = `/api/admin/credit-packages/bank-transfers/${transferId}`;
        const response = await axios.get(url);

        dispatch(doGetBankTransferDetailSuccess(response.data));
    } catch (error) {
        const errorMessage = error.response?.data?.message || "Failed to fetch transfer details";
        dispatch(doGetBankTransferDetailFail(errorMessage));
    }
};

export const doApproveBankTransfer = (transferId, adminNotes) => async (dispatch) => {
    try {
        dispatch(doApproveBankTransferRequest());

        const url = `/api/admin/credit-packages/bank-transfers/${transferId}/approve`;
        const response = await axios.post(url, { admin_notes: adminNotes });

        dispatch(doApproveBankTransferSuccess(response.data));
    } catch (error) {
        const errorMessage = error.response?.data?.message || "Failed to approve transfer";
        dispatch(doApproveBankTransferFail(errorMessage));
    }
};

export const doRejectBankTransfer = (transferId, adminNotes, rejectionReason) => async (dispatch) => {
    try {
        dispatch(doRejectBankTransferRequest());

        const url = `/api/admin/credit-packages/bank-transfers/${transferId}/reject`;
        const response = await axios.post(url, {
            admin_notes: adminNotes,
            rejection_reason: rejectionReason,
        });

        dispatch(doRejectBankTransferSuccess(response.data));
    } catch (error) {
        const errorMessage = error.response?.data?.message || "Failed to reject transfer";
        dispatch(doRejectBankTransferFail(errorMessage));
    }
};

// Selectors
export const getBankTransfersList = (state) => state.bankTransfers.listData;
export const getBankTransfersListLoading = (state) => state.bankTransfers.listLoading;
export const getBankTransfersListPage = (state) => state.bankTransfers.listPage;
export const getBankTransfersListPageCount = (state) => state.bankTransfers.listPageCount;
export const getBankTransfersListStatistics = (state) => state.bankTransfers.listStatistics;
export const getBankTransfersListError = (state) => state.bankTransfers.listErrorMessage;

export const getBankTransferDetail = (state) => state.bankTransfers.detailData;
export const getBankTransferDetailLoading = (state) => state.bankTransfers.detailLoading;
export const getBankTransferDetailError = (state) => state.bankTransfers.detailErrorMessage;

export const getApproveLoading = (state) => state.bankTransfers.approveLoading;
export const getApproveSuccess = (state) => state.bankTransfers.approveSuccess;
export const getApproveError = (state) => state.bankTransfers.approveErrorMessage;

export const getRejectLoading = (state) => state.bankTransfers.rejectLoading;
export const getRejectSuccess = (state) => state.bankTransfers.rejectSuccess;
export const getRejectError = (state) => state.bankTransfers.rejectErrorMessage;

export default bankTransfersSlice.reducer;